import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Alert,
  AlertTitle,
  CircularProgress,
  Divider,
  useTheme,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Tab from '@mui/material/Tab';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import {
  useGetFamilyTreesByUser,
  useRemoveSelfFromCollaborations,
} from '../../../hooks/treeHooks';
import {
  TextField,
  Button,
  Container,
  Chip,
  Switch,
  FormControlLabel,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import VerifiedIcon from '@mui/icons-material/Verified';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { useForm, Controller } from 'react-hook-form';
import { updateProfile, deleteUser } from 'firebase/auth';
import { Header } from '../../dashboard/components';
import { useAuth } from '../../../components/auth/AuthProvider';
import { useModal } from '../../../components/common';
import DeleteModal from '../../../components/common/DeleteModal';
import { AppConfig } from '../../../core/constants';
import ErrorDisplay from '../../../components/common/ErrorDisplay';
import useSubscription from '../../../hooks/useSubscription';
import { useManageSubscription } from '../../../hooks';
import { HTTPInstance } from '../../../core';

interface ProfileFormData {
  displayName: string;
  email: string;
  photoURL?: string;
  phoneNumber?: string;
}

export default function ProfileSettings() {
  const { palette, typography } = useTheme();
  const navigate = useNavigate();
  const { openModal, closeModal } = useModal();
  const [tab, setTab] = useState('1');

  const { sub, isPremium } = useSubscription();
  const [portalLoading, setPortalLoading] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [marketingPreferenceLoading, setMarketingPreferenceLoading] =
    useState(false);
  const { mutateAsync, isPending } = useManageSubscription();
  const { mutateAsync: removeSelfFromCollabs } =
    useRemoveSelfFromCollaborations();

  const { control, handleSubmit, setValue } = useForm<ProfileFormData>({
    defaultValues: {
      displayName: '',
      email: '',
      photoURL: '',
      phoneNumber: '',
    },
  });
  const [loading, setLoading] = useState(false);

  const { currentUser } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (currentUser) {
      setValue('displayName', currentUser.displayName || '');
      setValue('email', currentUser.email || '');
      setValue('phoneNumber', currentUser.phoneNumber || '');
    }
  }, [currentUser, setValue]);

  useEffect(() => {
    if (!currentUser?.uid) {
      setMarketingOptIn(false);
      return;
    }

    let isActive = true;
    setMarketingPreferenceLoading(true);

    HTTPInstance.get(`/users/${currentUser.uid}/marketing-preference`)
      .then((response) => {
        if (isActive) {
          setMarketingOptIn(response.data.marketing_opt_in);
        }
      })
      .catch((error) => {
        console.error('Failed to load marketing preference', error);
        if (isActive) {
          enqueueSnackbar('Could not load email preferences', {
            variant: 'error',
          });
        }
      })
      .finally(() => {
        if (isActive) {
          setMarketingPreferenceLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [currentUser?.uid, enqueueSnackbar]);

  const userId = currentUser?.uid;
  const {
    data: familyTrees,
    isLoading,
    isError,
    error: treeError,
    refetch: treeRefetch,
  } = useGetFamilyTreesByUser(userId ?? '', currentUser?.email ?? '');
  const treeData = familyTrees as any;
  const ownedTrees =
    treeData?.filter((tree: any) => tree.created_by === userId) ?? [];

  if (loading || isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100dvh',
          mt: '64px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }
  if (isError) {
    return <ErrorDisplay error={treeError} onRetry={treeRefetch} />;
  }

  const onSubmit = async (data: any) => {
    if (!currentUser) return;
    setLoading(true);
    try {
      await updateProfile(currentUser, { displayName: data.displayName });
      enqueueSnackbar('Profile updated successfully', {
        variant: 'success',
      });
    } catch (error) {
      console.error('Update failed', error);
      enqueueSnackbar('Failed to update profile', {
        variant: 'error',
      });
    }
    setLoading(false);
  };

  const handleDeleteAccount = async () => {
    if (!currentUser) return;

    try {
      await removeSelfFromCollabs();
      await deleteUser(currentUser);
      enqueueSnackbar('Account deleted successfully', {
        variant: 'success',
      });
      closeModal?.();
      navigate('/');
    } catch (error) {
      console.error('Delete failed', error);
      enqueueSnackbar('Failed to delete account', {
        variant: 'error',
      });
    }
  };

  function openAccDelete() {
    openModal(
      <DeleteModal
        closeModal={closeModal}
        onDelete={handleDeleteAccount}
        deleteMessage="Are you sure you want to delete your account? 
        This action cannot be undone."
        deleteTitle="Delete Account"
        familyTrees={ownedTrees}
      />,
    );
  }

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setTab(newValue);
  };

  const handleManageSubscription = async () => {
    try {
      setPortalLoading(isPending);
      const response = await mutateAsync();
      enqueueSnackbar('Billing portal created successfully', {
        variant: 'success',
      });
      let url: string | undefined = undefined;
      if (response && typeof response === 'object') {
        if ('url' in response && typeof response.url === 'string') {
          url = response.url;
        } else if (
          response.data &&
          typeof response.data === 'object' &&
          'url' in response.data &&
          typeof response.data.url === 'string'
        ) {
          url = response.data.url;
        }
      }
      if (url) {
        window.location.href = url;
      } else {
        enqueueSnackbar('Billing portal URL not found.', {
          variant: 'error',
        });
      }
    } catch (error) {
      console.error('Failed to open billing portal:', error);
      enqueueSnackbar('Failed to open billing portal', {
        variant: 'error',
      });
      setPortalLoading(false);
    }
  };
  const handleMarketingPreferenceChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const nextValue = event.target.checked;
    const previousValue = marketingOptIn;

    setMarketingOptIn(nextValue);
    setMarketingPreferenceLoading(true);

    try {
      await HTTPInstance.patch(`/users/${userId}/marketing-preference`, {
        marketing_opt_in: nextValue,
      });

      enqueueSnackbar(
        nextValue ? 'Marketing emails enabled' : 'Marketing emails disabled',
        { variant: 'success' },
      );
    } catch (error) {
      setMarketingOptIn(previousValue);
      enqueueSnackbar('Could not update email preferences', {
        variant: 'error',
      });
    } finally {
      setMarketingPreferenceLoading(false);
    }
  };
  return (
    <Box
      component="main"
      sx={{ flexGrow: 1, overflow: 'auto', width: '100%', height: '100dvh' }}
    >
      <Helmet>
        <title>Account | {AppConfig.appName}</title>
        <meta name="description" content="View your account settings" />
      </Helmet>
      <Stack
        spacing={2}
        sx={{
          alignItems: 'center',
          mx: { xs: 1, sm: 3 },
          pb: 5,
          mt: { xs: 8, md: 0 },
        }}
      >
        <Header title="Home" headerName="Account" />
        <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '1700px' } }}>
          <TabContext value={tab}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <TabList
                onChange={handleChange}
                aria-label="account-tabs"
                variant="scrollable"
                allowScrollButtonsMobile
              >
                <Tab label="Profile" value="1" />
                <Tab label="Billing" value="2" />
              </TabList>
            </Box>
            <TabPanel value="1" sx={{ px: { xs: 0, sm: 3 } }}>
              {' '}
              <Box
                sx={{ width: '100%', maxWidth: { sm: '100%', md: '1700px' } }}
              >
                <Container
                  maxWidth="md"
                  sx={{
                    bgcolor: palette.primary.contrastText,
                    py: 3,
                    borderRadius: 4,
                  }}
                >
                  <Box>
                    <Typography
                      variant="h2"
                      sx={{
                        fontSize: { xs: '1.75rem', sm: '2.5rem' },
                        fontFamily: '"Playfair Display", serif',
                        fontWeight: typography.h3.fontWeight,
                        color: 'text.primary',
                      }}
                    >
                      Personal Information
                    </Typography>
                    <Box textAlign="center" my={4}>
                      <Typography></Typography>
                    </Box>
                    <form onSubmit={handleSubmit(onSubmit)}>
                      <Controller
                        name="displayName"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Full Name"
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                            margin="normal"
                          />
                        )}
                      />
                      <Controller
                        name="email"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Email"
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                            margin="normal"
                            disabled
                          />
                        )}
                      />
                      <Controller
                        name="phoneNumber"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Phone Number"
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                            margin="normal"
                          />
                        )}
                      />
                      <Box my={2} display="flex" justifyContent="flex-end">
                        <Button
                          type="submit"
                          sx={{ width: { xs: '100%', sm: 'auto' } }}
                          variant="contained"
                          color="primary"
                          disabled={loading}
                        >
                          Save Changes
                        </Button>
                      </Box>
                    </form>
                  </Box>
                  <Divider sx={{ my: 4 }} />

                  <Box>
                    <Typography
                      variant="h5"
                      fontWeight={typography.h3.fontWeight}
                    >
                      Email preferences
                    </Typography>

                    <Typography
                      variant="body1"
                      color="textSecondary"
                      sx={{ mt: 1, mb: 2 }}
                    >
                      Receive occasional product updates, family-history tips,
                      and offers.
                    </Typography>

                    <FormControlLabel
                      control={
                        <Switch
                          checked={marketingOptIn}
                          disabled={marketingPreferenceLoading}
                          onChange={handleMarketingPreferenceChange}
                          color="primary"
                        />
                      }
                      label="Receive marketing emails"
                    />
                  </Box>
                  <Divider sx={{ my: 4 }} />
                  <Box>
                    <Typography
                      variant="h5"
                      sx={{ mt: 3 }}
                      fontWeight={typography.h3.fontWeight}
                    >
                      Close account
                    </Typography>
                    <Box
                      display="flex"
                      flexDirection={{ xs: 'column', sm: 'row' }}
                      justifyContent="space-between"
                      alignItems={{ xs: 'stretch', sm: 'center' }}
                      gap={2}
                      mt={1}
                    >
                      <Typography variant="body1" color="textSecondary">
                        Once your account is closed, you won’t be able to sign
                        in
                      </Typography>
                      <Button
                        variant="contained"
                        color="error"
                        onClick={openAccDelete}
                        disabled={loading}
                      >
                        Delete Account
                      </Button>
                    </Box>
                  </Box>
                </Container>
              </Box>
            </TabPanel>
            <TabPanel value="2" sx={{ px: { xs: 0, sm: 3 } }}>
              <Box
                sx={{ width: '100%', maxWidth: { sm: '100%', md: '1700px' } }}
              >
                <Container
                  maxWidth="md"
                  sx={{
                    bgcolor: palette.primary.contrastText,
                    py: 3,
                    borderRadius: 4,
                  }}
                >
                  <Box sx={{ mb: 6 }}>
                    <Typography
                      variant="overline"
                      color="primary"
                      sx={{ fontWeight: 700, letterSpacing: 1.2 }}
                    >
                      Membership Settings
                    </Typography>
                    <Typography
                      variant="h3"
                      sx={{
                        fontFamily: '"Playfair Display", serif',
                        mt: 1,
                        fontSize: { xs: '1.75rem', sm: '2.5rem' },
                      }}
                    >
                      Account & Billing
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      p: { xs: 2, sm: 3, md: 5 },
                      borderRadius: 4,
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Stack spacing={4}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          gap: 2,
                        }}
                      >
                        <Box>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                            gutterBottom
                          >
                            Current Plan
                          </Typography>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <Typography variant="h5" sx={{ fontWeight: 600 }}>
                              {isPremium ? 'The Roots (Builder Plan)' : 'Free'}
                            </Typography>
                            {isPremium && (
                              <VerifiedIcon color="primary" fontSize="small" />
                            )}
                          </Stack>
                        </Box>

                        <Chip
                          label={isPremium ? 'Active' : 'Basic'}
                          color={isPremium ? 'success' : 'default'}
                          variant="filled"
                          sx={{ fontWeight: 700, px: 1 }}
                        />
                      </Box>

                      <Divider />

                      <Box>
                        {sub?.subscription_status === 'active' &&
                          sub?.current_period_end && (
                            <Stack spacing={2.5}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1.5,
                                }}
                              >
                                <CalendarMonthIcon
                                  color="action"
                                  fontSize="small"
                                />
                                <Typography variant="body1">
                                  Your next billing date is{' '}
                                  <strong>
                                    {new Date(
                                      sub?.current_period_end,
                                    ).toLocaleDateString(undefined, {
                                      dateStyle: 'long',
                                    })}
                                  </strong>
                                </Typography>
                              </Box>

                              {sub?.cancel_at_period_end ||
                                (sub?.cancel_at && (
                                  <Alert
                                    severity="warning"
                                    variant="outlined"
                                    sx={{ borderRadius: 2 }}
                                  >
                                    <AlertTitle>
                                      Subscription Cancelling
                                    </AlertTitle>
                                    Your access will remain active until the end
                                    of the current period.{' '}
                                    <strong>
                                      {new Date(
                                        sub?.current_period_end,
                                      ).toLocaleDateString(undefined, {
                                        dateStyle: 'long',
                                      })}
                                    </strong>
                                  </Alert>
                                ))}
                            </Stack>
                          )}

                        {sub?.oneoff_setup_paid && (
                          <Typography variant="body1">
                            One-time setup support (Assisted Legacy Launch):{' '}
                            <strong>Purchased</strong>
                          </Typography>
                        )}

                        {sub?.subscription_status === 'past_due' && (
                          <Alert
                            severity="error"
                            variant="filled"
                            sx={{ borderRadius: 2 }}
                          >
                            <AlertTitle>Action Required</AlertTitle>
                            Your latest payment failed. Please update your
                            payment details to maintain access.
                          </Alert>
                        )}

                        {!isPremium && (
                          <Typography variant="body1" color="text.secondary">
                            You are currently on the free tier. Upgrade to
                            preserve more memories.
                          </Typography>
                        )}
                      </Box>

                      <Box
                        sx={{
                          pt: 2,
                          display: 'flex',
                          justifyContent: 'flex-end',
                        }}
                      >
                        {isPremium ? (
                          <Button
                            variant="contained"
                            size="large"
                            startIcon={<OpenInNewIcon />}
                            onClick={handleManageSubscription}
                            disabled={portalLoading}
                            loading={portalLoading}
                            sx={{
                              borderRadius: 2,
                              textTransform: 'none',
                              fontWeight: 600,
                              px: 4,
                              width: { xs: '100%', sm: 'auto' },
                            }}
                          >
                            {portalLoading
                              ? 'Opening Portal...'
                              : 'Manage Billing & Invoices'}
                          </Button>
                        ) : (
                          <Button
                            variant="contained"
                            size="large"
                            sx={{
                              borderRadius: 2,
                              px: 4,
                              width: { xs: '100%', sm: 'auto' },
                            }}
                            onClick={() => navigate('/pricing')}
                          >
                            Upgrade Plan
                          </Button>
                        )}
                      </Box>
                    </Stack>
                  </Box>
                  <Typography
                    variant="subtitle1"
                    color="info"
                    sx={{ mt: 3, display: 'block', textAlign: 'center' }}
                  >
                    Payments are securely processed by Stripe.
                  </Typography>
                </Container>
              </Box>
            </TabPanel>
          </TabContext>
        </Box>
      </Stack>
    </Box>
  );
}
